import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { Menu, X, User, LogOut, Heart, LayoutDashboard, Bell, Check, Clock, Shield, Calendar, FileCheck, Settings, ChevronDown, Activity } from 'lucide-react';
import CountryFlag from './CountryFlag';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NotificationIcon = ({ type }) => {
  switch (type) {
    case 'booking': return <Calendar className="h-4 w-4 text-[#E07A3A]" />;
    case 'verification': return <FileCheck className="h-4 w-4 text-green-600" />;
    case 'gdpr': return <Shield className="h-4 w-4 text-blue-600" />;
    case 'admin': return <Shield className="h-4 w-4 text-amber-600" />;
    case 'nudge': return <Bell className="h-4 w-4 text-red-500" />;
    case 'consultation': return <FileCheck className="h-4 w-4 text-violet-600" />;
    default: return <Bell className="h-4 w-4 text-slate-500" />;
  }
};

const FALLBACK_POLL_MS = 30000;
const WS_RECONNECT_MS = 5000;
const MS_PER_MINUTE = 60000;

// Build WebSocket URL from API URL
const getWsUrl = (token) => {
  const base = API_URL.replace(/^http/, 'ws');
  return `${base}/api/ws/notifications?token=${encodeURIComponent(token)}`;
};

const Header = () => {
  const { user, logout } = useAuth();
  const { country, setCountry, countries } = useCountry();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const notifRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/unread-count`);
      setUnreadCount(res.data.count);
    } catch (e) {
      logger.error('Failed to fetch notification count:', e);
    }
  }, []);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!user) return;
    // Read token from sessionStorage (base64-obfuscated)
    const TOKEN_KEY = 'shopperden_access_token';
    const stored = sessionStorage.getItem(TOKEN_KEY);
    if (!stored) return;
    let rawToken;
    try { rawToken = atob(stored); } catch { rawToken = stored; }

    const wsUrl = getWsUrl(rawToken);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        logger.info('WS connected');
        // Start ping keep-alive every 25s
        if (pingTimer.current) clearInterval(pingTimer.current);
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.event === 'connected') {
            setUnreadCount(msg.data?.unread_count || 0);
          } else if (msg.event === 'notification') {
            const notif = msg.data;
            setUnreadCount(prev => prev + 1);
            // Merge into notification list so history is preserved
            setNotifications(prev => {
              const exists = prev.some(n => n.id === notif.id);
              if (exists) return prev;
              return [notif, ...prev].slice(0, 50);
            });
            // Toast with click-to-navigate action
            toast(notif.title, {
              description: notif.message,
              duration: 6000,
              action: notif.link ? {
                label: 'View',
                onClick: () => navigate(notif.link),
              } : undefined,
            });
          }
        } catch (e) {
          logger.error('WS message parse error:', e);
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        if (pingTimer.current) clearInterval(pingTimer.current);
        // Auto-reconnect after delay
        if (user) {
          reconnectTimer.current = setTimeout(connectWebSocket, WS_RECONNECT_MS);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      logger.error('WS connection failed:', e);
    }
  }, [user, navigate]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(res.data);
    } catch (e) {
      logger.error('Failed to fetch notifications:', e);
    }
  }, []);

  // Connect WebSocket when user logs in
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Pre-fetch notification history so bell panel shows it immediately
      fetchNotifications();
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pingTimer.current) clearInterval(pingTimer.current);
    };
  }, [user, fetchUnreadCount, fetchNotifications, connectWebSocket]);

  // Fallback polling — only when WebSocket is NOT connected
  useEffect(() => {
    if (!user || wsConnected) return;
    const interval = setInterval(fetchUnreadCount, FALLBACK_POLL_MS);
    return () => clearInterval(interval);
  }, [user, wsConnected, fetchUnreadCount]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleNotifications = () => {
    if (!showNotifications) fetchNotifications();
    setShowNotifications(!showNotifications);
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notifId}/read`, {});
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      logger.error('Failed to mark notification as read:', e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {});
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {
      logger.error('Failed to mark all notifications as read:', e);
    }
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    if (notif.link) navigate(notif.link);
    setShowNotifications(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'seller') return '/seller';
    if (user.role === 'agent') return '/agent';
    if (user.role === 'professional') return '/professional';
    if (user.role === 'tenant') return '/tenant';
    return '/buyer';
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / MS_PER_MINUTE);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <header className="luxury-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link" aria-label="Shopperden home">
            <span
              aria-hidden="true"
              className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm transition-transform duration-200 group-hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #10b981 0%, #0f766e 100%)' }}
            >
              <svg viewBox="0 0 64 64" className="w-6 h-6" fill="none" stroke="#ffffff" strokeLinejoin="round" strokeLinecap="round">
                <path d="M25 17 Q25 10 32 10 Q39 10 39 17" strokeWidth="2.6" />
                <path d="M14 32 L32 17 L50 32 L50 50 L14 50 Z" strokeWidth="3.6" />
                <path d="M28 50 L28 38 L36 38 L36 50" strokeWidth="3.6" />
              </svg>
            </span>
            <span className="font-['Playfair_Display'] text-xl tracking-wide text-white font-semibold">
              SHOPPER<span className="text-[#D4AF37]">DEN</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-3 xl:gap-6 2xl:gap-8 flex-wrap justify-end flex-1 min-w-0">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-white/70 hover:text-[#D4AF37] transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase font-semibold outline-none" data-testid="nav-properties">
                Properties <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white border-slate-200 min-w-[220px]">
                <DropdownMenuItem asChild>
                  <Link to="/properties" className="cursor-pointer text-sm tracking-wider" data-testid="nav-all-listings">All Listings</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/properties/uk" className="cursor-pointer text-sm tracking-wider" data-testid="nav-aggregate-uk">UK · Aggregate Listings</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/properties/india" className="cursor-pointer text-sm tracking-wider" data-testid="nav-aggregate-in">India · Aggregate Listings</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/properties/canada" className="cursor-pointer text-sm tracking-wider" data-testid="nav-aggregate-ca">Canada · Aggregate Listings</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-white/70 hover:text-[#D4AF37] transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase font-semibold outline-none" data-testid="nav-guides">
                Guides <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white border-slate-200 min-w-[180px]">
                <DropdownMenuItem asChild>
                  <Link to="/buying-guide" className="cursor-pointer text-sm tracking-wider" data-testid="nav-buying-guide">Buying Guide</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/selling-guide" className="cursor-pointer text-sm tracking-wider" data-testid="nav-selling-guide">Selling Guide</Link>
                </DropdownMenuItem>
                {country === 'IN' && (
                  <DropdownMenuItem asChild>
                    <Link to="/tenant-guide" className="cursor-pointer text-sm tracking-wider" data-testid="nav-tenant-guide">Tenant Guide</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/mortgage-guide" className="cursor-pointer text-sm tracking-wider" data-testid="nav-mortgage-guide">Mortgage Guide</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/blog" className="text-white/70 hover:text-[#D4AF37] transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase font-semibold" data-testid="nav-blog">
              Blog
            </Link>
            <Link to="/pulse" className="text-white/70 hover:text-[#D4AF37] transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase font-semibold" data-testid="nav-pulse">
              Pulse Reports
            </Link>
            <Link to="/find-a-professional" className="text-white/70 hover:text-[#D4AF37] transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase font-semibold" data-testid="nav-find-pro">
              Find a Pro
            </Link>
            <Link to="/for-agents" className="text-white/70 hover:text-[#D4AF37] transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase font-semibold" data-testid="nav-for-agents">
              For Agents
            </Link>
            <Link
              to="/affiliate"
              className="text-emerald-300 hover:text-emerald-200 transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase font-bold flex items-center gap-1.5"
              data-testid="nav-affiliate"
            >
              <span className="text-[9px] bg-emerald-500/20 border border-emerald-400/40 px-1.5 py-0.5 rounded">FREE</span>
              Earn 25%
            </Link>
            <Link to="/contact" className="text-white/70 hover:text-[#D4AF37] transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase font-semibold" data-testid="nav-contact">
              Contact
            </Link>
            {user && (
              <Link to="/favorites" className="text-white/70 hover:text-[#D4AF37] transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase flex items-center gap-2 font-semibold" data-testid="nav-favorites">
                <Heart className="h-4 w-4" />
                Favorites
              </Link>
            )}
            {user && (
              <Link to={getDashboardLink()} className="text-[#D4AF37] hover:text-[#E5C865] transition-colors text-[13px] xl:text-base tracking-wider xl:tracking-widest uppercase flex items-center gap-2 font-semibold" data-testid="nav-dashboard">
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Link>
            )}
            {/* Country Selector */}
            <div className="flex items-center gap-1 px-1.5 py-1 border border-white/20 rounded-full bg-white/5 backdrop-blur-sm" data-testid="country-selector">
              {Object.values(countries).map((c) => (
                <button
                  key={c.code}
                  onClick={() => setCountry(c.code)}
                  className={`relative px-2 py-1 rounded-full transition-all ${country === c.code ? 'bg-[#D4AF37]/15 ring-2 ring-[#D4AF37]/70 scale-110 shadow-sm' : 'hover:bg-white/10'}`}
                  title={c.name}
                  data-testid={`country-flag-${c.code}`}
                >
                  <CountryFlag code={c.code} title={c.name} className="w-6 h-4 inline-block align-middle rounded-sm overflow-hidden shadow-[0_0_0_1px_rgba(0,0,0,0.08)]" />
                </button>
              ))}
            </div>
          </nav>

          {/* Desktop Auth + Notifications */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3">
            {user ? (
              <>
                {/* Notification Bell */}
                <div className="relative" ref={notifRef}>
                  <button
                    onClick={toggleNotifications}
                    className="relative w-10 h-10 flex items-center justify-center text-white/70 hover:text-[#D4AF37] transition-colors"
                    data-testid="notification-bell"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#D4AF37] text-[#0A0A0C] text-xs flex items-center justify-center rounded-full font-bold" data-testid="notif-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Panel */}
                  {showNotifications && (
                    <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-slate-200 shadow-xl z-50" style={{ color: '#0F172A' }} data-testid="notification-panel">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
                        <h3 className="text-slate-900 font-bold text-sm">Notifications</h3>
                        {unreadCount > 0 && (
                          <button onClick={markAllRead} className="text-[#E07A3A] text-xs font-semibold hover:text-[#C4652E]" data-testid="mark-all-read-btn">
                            Mark all read
                          </button>
                        )}
                      </div>
                      <div className="max-h-96 overflow-y-auto bg-white">
                        {notifications.length > 0 ? notifications.map((notif) => (
                          <button
                            key={notif.id}
                            onClick={() => handleNotifClick(notif)}
                            className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-[#FEF3EC] transition-colors flex gap-3 ${!notif.read ? 'bg-[#FEF3EC]/40' : 'bg-white'}`}
                            data-testid={`notif-${notif.id}`}
                          >
                            <div className="mt-0.5 flex-shrink-0">
                              <NotificationIcon type={notif.type} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.read ? 'text-slate-900 font-bold' : 'text-slate-800 font-medium'}`}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-slate-700 mt-1 line-clamp-2 leading-snug">{notif.message}</p>
                              <p className="text-xs text-slate-500 mt-1.5">{timeAgo(notif.created_at)}</p>
                            </div>
                            {!notif.read && <div className="w-2 h-2 bg-[#E07A3A] rounded-full mt-2 flex-shrink-0" />}
                          </button>
                        )) : (
                          <div className="px-4 py-8 text-center text-slate-500 text-sm bg-white">
                            No notifications yet
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-3 text-white/80 hover:text-[#D4AF37] hover:bg-white/5" data-testid="user-menu-trigger">
                      <div className="w-10 h-10 border border-[#D4AF37]/50 flex items-center justify-center">
                        <User className="h-4 w-4 text-[#D4AF37]" />
                      </div>
                      <span className="text-sm tracking-wide font-medium">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-white border-slate-200">
                    <DropdownMenuItem onClick={() => navigate(getDashboardLink())} className="cursor-pointer text-slate-600 hover:text-[#1A1A1A] focus:text-[#1A1A1A] focus:bg-slate-50" data-testid="menu-dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" />Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/favorites')} className="cursor-pointer text-slate-600 hover:text-[#1A1A1A] focus:text-[#1A1A1A] focus:bg-slate-50" data-testid="menu-favorites">
                      <Heart className="mr-2 h-4 w-4" />Favorites
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/pulse')} className="cursor-pointer text-slate-600 hover:text-[#1A1A1A] focus:text-[#1A1A1A] focus:bg-slate-50" data-testid="menu-plans-fees">
                      <Activity className="mr-2 h-4 w-4" />Plans & Fees
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-account')} className="cursor-pointer text-slate-600 hover:text-[#1A1A1A] focus:text-[#1A1A1A] focus:bg-slate-50" data-testid="menu-my-account">
                      <Settings className="mr-2 h-4 w-4" />My Account
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-200" />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-500 hover:text-red-600 focus:text-red-600 focus:bg-red-50 py-3" data-testid="menu-logout">
                      <LogOut className="mr-2 h-4 w-4" />Sign Out of Shopperden
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Visible Sign Out Button — always accessible */}
                <button
                  onClick={handleLogout}
                  className="ml-1 px-3 h-10 flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-white/60 hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded transition-all border border-white/10 hover:border-[#EF4444]/40"
                  title="Sign Out"
                  data-testid="visible-logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden lg:inline">Sign Out</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-white/80 hover:text-[#D4AF37] hover:bg-white/5 tracking-widest text-sm font-semibold" data-testid="login-btn">
                    SIGN IN
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#D4AF37] text-[#0A0A0C] hover:bg-[#E5C865] tracking-widest text-sm px-6 font-bold" data-testid="register-btn">
                    SIGN UP
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Mobile Country Selector */}
            <div className="flex items-center border border-white/20 rounded-full overflow-hidden bg-white/5" data-testid="mobile-country-selector">
              {Object.values(countries).map((c) => (
                <button
                  key={c.code}
                  onClick={() => setCountry(c.code)}
                  className={`relative px-2 py-1 rounded-full transition-all ${country === c.code ? 'bg-[#D4AF37]/15 ring-2 ring-[#D4AF37]/70 scale-110' : 'hover:bg-white/10'}`}
                  data-testid={`mobile-country-flag-${c.code}`}
                >
                  <img src={c.flag} alt={c.name} width="20" height="14" className="w-5 h-3.5 object-cover rounded-sm shadow-[0_0_0_1px_rgba(255,255,255,0.15)]" />
                </button>
              ))}
            </div>
            {user && (
              <div className="relative" ref={!showNotifications ? undefined : notifRef}>
                <button onClick={toggleNotifications} className="relative w-10 h-10 flex items-center justify-center text-white/70" data-testid="mobile-notification-bell">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#D4AF37] text-[#0A0A0C] text-xs flex items-center justify-center rounded-full font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </div>
            )}
            <button className="text-white p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-white/10 bg-[#0A0A0C]/95 backdrop-blur-xl -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <nav className="flex flex-col gap-1">
              <Link to="/properties" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-properties">
                Properties
              </Link>
              <Link to="/buying-guide" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-buying-guide">
                Buying Guide
              </Link>
              <Link to="/selling-guide" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-selling-guide">
                Selling Guide
              </Link>
              {country === 'IN' && (
                <Link to="/tenant-guide" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-tenant-guide">
                  Tenant Guide
                </Link>
              )}
              <Link to="/mortgage-guide" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-mortgage">
                Mortgage Guide
              </Link>
              <Link to="/blog" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-blog">
                Blog
              </Link>
              <Link to={country === 'IN' ? '/properties/india' : country === 'CA' ? '/properties/canada' : '/properties/uk'} className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-aggregate-listings">
                Aggregate Listings ({country === 'IN' ? 'India' : country === 'CA' ? 'Canada' : 'UK'})
              </Link>
              <Link to="/pulse" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-pulse">
                Pulse Reports · Download PDF
              </Link>
              <Link to="/find-a-professional" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-find-pro">
                Find a Professional
              </Link>
              <Link to="/for-agents" className="text-[#E07A3A] hover:text-[#C4652E] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-for-agents">
                For Agents →
              </Link>
              <Link
                to="/affiliate"
                className="text-emerald-300 hover:text-emerald-200 py-3 px-2 tracking-widest text-sm uppercase font-bold flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-affiliate"
              >
                <span className="text-[10px] bg-emerald-500/20 border border-emerald-400/40 px-2 py-0.5 rounded">FREE</span>
                Earn 25% — Affiliate Programme →
              </Link>
              <Link to="/contact" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-contact">
                Contact
              </Link>
              {user ? (
                <>
                  <Link to="/favorites" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-favorites">
                    Favorites
                  </Link>
                  <Link to="/my-account" className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-my-subscription">
                    My Subscription
                  </Link>
                  <Link to={getDashboardLink()} className="text-white/70 hover:text-[#D4AF37] py-3 px-2 tracking-widest text-sm uppercase font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-dashboard">
                    Dashboard
                  </Link>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex items-center justify-center gap-3 text-red-500 hover:text-red-600 hover:bg-red-50 py-4 px-3 rounded-lg text-left tracking-widest text-sm uppercase font-bold mt-2 border border-red-200 w-full" data-testid="mobile-logout-btn">
                    <LogOut className="h-4 w-4" />
                    Sign Out of Shopperden
                  </button>
                </>
              ) : (
                <div className="flex gap-3 pt-4 px-2">
                  <Link to="/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full border-[#E07A3A] text-[#E07A3A] font-semibold" data-testid="mobile-login-link">SIGN IN</Button>
                  </Link>
                  <Link to="/register" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] font-semibold" data-testid="mobile-register-link">SIGN UP</Button>
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
